import {get, post, put} from '@/services/infrastructure/api.service'
import emitter from '@/services/infrastructure/emitter'
import {
    Assignment,
    AssignmentDependency,
    AssignmentSchema,
    AssignmentsSchema,
    AssignmentStatus,
    CreateAssignmentCommand,
    Fulfillment,
    UpdateAssignmentCommand,
} from '@/assignments/Assignment.type'
import type {QueryRequest} from '@/query/QueryRequest'
import {ref} from 'vue'

const ASSIGNMENT_ROOT = '/assignments'

const ASSIGNMENT_CREATED = 'assignmentCreated'
const ASSIGNMENT_UPDATED = 'assignmentUpdated'

const ASSIGNMENTS_CHANGED_EVENTS = [ASSIGNMENT_CREATED, ASSIGNMENT_UPDATED]

const detailsVisible = ref(false)

const fetchAssignments = (request: QueryRequest): Promise<Assignment[]> => post('/assignments/:getrows', request).then(
    (res) => res && AssignmentsSchema.parse(res)
)

const findByAssignmentUuid = async (assignmentUuid: string) => {
    const response = await get<Assignment>(`${ASSIGNMENT_ROOT}/${assignmentUuid}`)
    return response && AssignmentSchema.parse(response)
}

export const fetchDependencies = async (
    assignment: Assignment,
    filterOptions: { statuses: Assignment['status'][] } = {statuses: [AssignmentStatus.Pending]}
) => {
    if (!assignment) {
        return []
    }
    const dependencyRequests = assignment.dependencies.map((dep) => findByAssignmentUuid(dep.uuid))
    const responses = await Promise.all(dependencyRequests)
    const filtered: Assignment[] = []
    responses.forEach((dep) => {
        if (!dep) {
            return
        }
        if (!filterOptions.statuses.some((status) => dep.status === status)) {
            return
        }
        filtered.push(dep)
    })
    return filtered
}

const deleteAssignment = (uuid: string) => post('/assignments/:delete', {uuid})
    .then((res) => {
        emitter.emit(ASSIGNMENT_UPDATED)
        return res && AssignmentSchema.parse(res)
    })

const completeAssignment = (uuid: string): Promise<Assignment> => post('/assignments/:complete', {uuid})
    .then((res) => {
        emitter.emit(ASSIGNMENT_UPDATED)
        return res && AssignmentSchema.parse(res)
    })

const createAssignment = (cmd: CreateAssignmentCommand): Promise<Assignment> => post('/assignments', cmd)
    .then((res) => {
        return res && AssignmentSchema.parse(res)
    })

//TODO: Fix assignment typing ??
const updateAssignment = async (assignment: any): Promise<Assignment> => {
    const cmd: UpdateAssignmentCommand = {
        assignmentNo: assignment.assignmentNo,
        createdAt: assignment.createdAt,
        type: assignment.type,
        uuid: assignment.uuid,
        title: assignment.title,
        description: assignment.description,
        resourceLinks: assignment.resourceLinks,
        assignee: assignment.assignee,
        status: assignment.status,
        context: assignment.context,
        dependencies: assignment.dependencies.map((dep: AssignmentDependency) => dep.uuid),
        dimensions: assignment.dimensions || []
    }

    return put('/assignments', cmd)
        .then((res) => {
            emitter.emit(ASSIGNMENT_UPDATED)
            return res && AssignmentSchema.parse(res)
        })
}

const resumeAssignment = async (uuid: string): Promise<Assignment> => {
    const res = await post(`${ASSIGNMENT_ROOT}/${uuid}/resume`, {})
    emitter.emit(ASSIGNMENT_UPDATED)
    return res && AssignmentSchema.parse(res)
}

const rejectAssignment = (uuid: string): Promise<Assignment> => post(`/assignments/${uuid}/reject`, {})
    .then((res) => {
        emitter.emit(ASSIGNMENT_UPDATED)
        return res && AssignmentSchema.parse(res)
    })

const createIncomingDocumentAssignment = (documentMetaUuid: string) => post('/assignments/:create-bookkeep-document', {documentMetaUuid})

const getAssignmentAttachmentUuids = (assignment: Assignment) => {
    //TODO: return docMetaUuids from resourceLinks, not from actionable
    //return assignment?.resourceLinks?.map(rl => rl.identifier)

    if (assignment?.context?.type !== 'Bookkeepable')
        return []
    return assignment?.context.documentMetaUuids || []
}

const assignmentToFulfillment = (assignment: Assignment, close: boolean = true): Fulfillment => {
    return {
        assignmentUuid: assignment.uuid,
        title: assignment.title,
        description: assignment.description,
        fulfillOnSuccess: close,
        assignmentNo: assignment.assignmentNo
    }
}

export {
    ASSIGNMENT_CREATED,
    ASSIGNMENT_UPDATED,
    ASSIGNMENTS_CHANGED_EVENTS,
    createAssignment,
    updateAssignment,
    createIncomingDocumentAssignment,
    fetchAssignments,
    findByAssignmentUuid,
    deleteAssignment,
    completeAssignment,
    resumeAssignment,
    rejectAssignment,
    assignmentToFulfillment,
    getAssignmentAttachmentUuids,
    detailsVisible
}